/**
 * Deterministic layered family layout (section 9.2).
 *
 * Not a force simulation: generations sit on fixed horizontal levels, partners
 * are placed side by side as a couple unit, and children are centred under
 * their parents. The same data and focus always produce the same coordinates,
 * so a person does not move between visits, and an edit moves as little of the
 * tree as possible.
 *
 * The algorithm is a compact Sugiyama-style pass:
 *   1. assign generation levels (from the relationship graph)
 *   2. group partners into "units" that occupy one slot
 *   3. order units within each level to reduce crossings (barycentre sweeps;
 *      siblings — equal barycentres — go oldest → youngest, then by id, so the
 *      result is reproducible)
 *   4. assign x positions: children centred under parents, then resolve
 *      overlaps left-to-right
 *
 * The result is the *resting* position of every circle. On screen, circles that
 * have grown (fresh activity) push their row-mates aside a little; that live
 * adjustment lives in treeRelax.ts and never changes this layout.
 *
 * If this ever needs to handle much larger families or fancier edge routing,
 * this module is the one place to swap for ELK.js — the rest of the tree UI
 * only consumes the `TreeLayout` result. See docs/ARCHITECTURE.md.
 */
import { generations, parents, partners, children, partnerKey, type FamilyGraph } from '../../domain/relationships';
import type { Relationship, UUID } from '../../domain/types';

/*
 * Spacing. The circle is 104px; the node box is just wide enough for it and a
 * two-line name underneath. Rows and neighbours sit close — a grown circle (see
 * `relaxRows` in treeRelax.ts) pushes its neighbours aside on the fly, so the
 * resting layout no longer needs room for the biggest a circle can get.
 */
export const NODE_W = 120;
export const NODE_H = 178;
export const H_GAP = 16;
export const V_GAP = 64;
export const PARTNER_GAP = 0;
/** The circle sits at the top of the node; its centre is this far above the node centre. */
export const AVATAR_R = 52;
export const AVATAR_DY = -(NODE_H / 2) + 8 + AVATAR_R;

export interface LaidOutNode {
  id: UUID;
  x: number;
  y: number;
  level: number;
  unitId: string;
  /** Stable per-person offsets so the gentle float is not synchronised. */
  floatSeed: number;
}

export interface LaidOutEdge {
  id: string;
  kind: 'parent' | 'partner';
  from: UUID;
  to: UUID;
  relationship: Relationship;
  /** SVG path in layout coordinates. */
  d: string;
  /** Midpoint, used for labels/markers. */
  mid: { x: number; y: number };
  pending: boolean;
  dashed: boolean;
}

export interface TreeLayout {
  nodes: Map<UUID, LaidOutNode>;
  edges: LaidOutEdge[];
  width: number;
  height: number;
  levels: number;
  minX: number;
  minY: number;
  /** "By age" arrangement only: the year scale drawn beside the tree (see layoutByAge.ts). */
  yearMarks?: Array<{ year: number; y: number }>;
}

interface Unit {
  id: string;
  members: UUID[];
  level: number;
  order: number;
  x: number;
}

function hashSeed(id: string): number {
  let h = 2166136261;
  for (let i = 0; i < id.length; i++) h = Math.imul(h ^ id.charCodeAt(i), 16777619) >>> 0;
  return (h % 1000) / 1000;
}

/**
 * @param birthYear  Best-guess birth year per person (null = unknown). Within a
 *   row, people are ordered oldest → youngest left to right; a couple counts as
 *   the average of the two. Ties, and everyone without a year, fall back to id.
 */
export function layoutTree(graph: FamilyGraph, visibleIds: UUID[], relationships: Relationship[], birthYear: (id: UUID) => number | null = () => null): TreeLayout {
  const ids = visibleIds.filter((id) => graph.personIds.has(id));
  const idSet = new Set(ids);
  const empty: TreeLayout = { nodes: new Map(), edges: [], width: 0, height: 0, levels: 0, minX: 0, minY: 0 };
  if (!ids.length) return empty;

  // 1. levels
  const gen = generations(graph, ids);
  // Somebody whose own parents are not in the tree, but whose children are (a
  // spouse's father, say), would otherwise float up to the founders' row, far
  // above their own family. Sit them just above their eldest child instead —
  // then settle the usual rules again (partners share a row, children sit
  // below parents). Levels only ever move down here, so this terminates.
  const settle = () => {
    let changed = true;
    let guard = 0;
    while (changed && guard++ < ids.length + 5) {
      changed = false;
      for (const id of ids) {
        for (const p of parents(graph, id)) {
          if (!idSet.has(p)) continue;
          const want = gen.get(p)! + 1;
          if (want > gen.get(id)!) { gen.set(id, want); changed = true; }
        }
        for (const s of partners(graph, id)) {
          if (!idSet.has(s)) continue;
          const gs = gen.get(s)!;
          const gi = gen.get(id)!;
          if (gs > gi) { gen.set(id, gs); changed = true; } else if (gi > gs) { gen.set(s, gi); changed = true; }
        }
      }
    }
  };
  {
    let moved = true;
    let guard = 0;
    while (moved && guard++ < ids.length + 5) {
      moved = false;
      for (const id of ids) {
        // Only when nobody in the couple has parents in view — a partner with
        // ancestry keeps the couple on the row those ancestors dictate.
        const hasParents = (x: UUID) => parents(graph, x).some((p) => idSet.has(p));
        if (hasParents(id) || partners(graph, id).some((s) => idSet.has(s) && hasParents(s))) continue;
        const kids = children(graph, id).filter((c) => idSet.has(c));
        if (!kids.length) continue;
        const want = Math.min(...kids.map((c) => gen.get(c) ?? 0)) - 1;
        if (want > (gen.get(id) ?? 0)) { gen.set(id, want); moved = true; }
      }
      if (moved) settle();
    }
  }
  // Somebody with no relationships in view (typically a person who just joined
  // and has not been connected yet) would otherwise land on the top row beside
  // the oldest generation, far from everyone. Put them on the youngest row
  // instead, right after the last family, so they can see the tree they are
  // about to join.
  const connected = (id: UUID) => [...parents(graph, id), ...children(graph, id), ...partners(graph, id)].some((o) => idSet.has(o));
  const maxLevel = Math.max(0, ...[...gen.values()]);
  for (const id of ids) if (!connected(id)) gen.set(id, maxLevel);

  // 2. partner units
  const unitOf = new Map<UUID, string>();
  const units = new Map<string, Unit>();
  const sortedIds = [...ids].sort();
  const inViewParents = (x: UUID) => parents(graph, x).filter((p) => idSet.has(p)).length;
  for (const id of sortedIds) {
    if (unitOf.has(id)) continue;
    const level = gen.get(id) ?? 0;
    // Everyone joined by partner links on this level is one unit — a couple, or
    // a chain like "former wife — him — current wife", so remarriages stay next
    // to the shared children and each partner line only spans neighbours.
    const mates = (x: UUID) => partners(graph, x).filter((m) => idSet.has(m) && (gen.get(m) ?? 0) === level).sort();
    const group = new Set<UUID>([id]);
    const stack = [id];
    while (stack.length) {
      const cur = stack.pop()!;
      for (const m of mates(cur)) if (!group.has(m)) { group.add(m); stack.push(m); }
    }
    // Left-to-right order: walk the chain from one end. A two-person couple puts
    // the partner with parents in view first, so their own ancestry line is
    // short; a longer chain starts from the end that satisfies the same rule.
    const ends = [...group].filter((m) => mates(m).filter((o) => group.has(o)).length <= 1);
    const start = (ends.length ? ends : [...group]).sort((a, b) => {
      const pa = inViewParents(a);
      const pb = inViewParents(b);
      if (pa !== pb) return pb - pa;
      return a < b ? -1 : 1;
    })[0];
    const members: UUID[] = [];
    const seen = new Set<UUID>();
    const walk = (cur: UUID) => {
      seen.add(cur);
      members.push(cur);
      for (const m of mates(cur)) if (group.has(m) && !seen.has(m)) walk(m);
    };
    walk(start);
    for (const m of [...group].sort()) if (!seen.has(m)) walk(m);
    const unitId = `u:${members[0]}`;
    for (const m of members) unitOf.set(m, unitId);
    units.set(unitId, { id: unitId, members, level, order: 0, x: 0 });
  }

  const levels: Unit[][] = [];
  for (const u of units.values()) {
    (levels[u.level] ??= []).push(u);
  }
  for (let i = 0; i < levels.length; i++) levels[i] ??= [];

  // 3. ordering — barycentre sweeps with stable tie-break
  const unitParents = (u: Unit): string[] => {
    const out = new Set<string>();
    for (const m of u.members) for (const p of parents(graph, m)) if (idSet.has(p)) { const pu = unitOf.get(p); if (pu && pu !== u.id) out.add(pu); }
    return [...out];
  };
  const unitChildren = (u: Unit): string[] => {
    const out = new Set<string>();
    for (const m of u.members) for (const c of children(graph, m)) if (idSet.has(c)) { const cu = unitOf.get(c); if (cu && cu !== u.id) out.add(cu); }
    return [...out];
  };

  // Age of a unit: the mean birth year of its members (a couple = the average of the two),
  // so the older sibling — or the older couple — sits further left. Unknown → after the known.
  const unitAge = new Map<string, number>();
  for (const u of units.values()) {
    const years = u.members.map(birthYear).filter((y): y is number => y !== null && Number.isFinite(y));
    unitAge.set(u.id, years.length ? years.reduce((a, b) => a + b, 0) / years.length : Number.POSITIVE_INFINITY);
  }
  /** Oldest first; same age (or both unknown) → stable by id. */
  const byAge = (a: Unit, b: Unit): number => {
    const ya = unitAge.get(a.id) ?? Number.POSITIVE_INFINITY;
    const yb = unitAge.get(b.id) ?? Number.POSITIVE_INFINITY;
    if (ya !== yb) return ya < yb ? -1 : 1;
    return a.members[0] < b.members[0] ? -1 : 1;
  };

  // initial order: oldest to youngest along the row (the barycentre sweeps below
  // then pull families together while keeping this order among siblings)
  const isolated = (u: Unit) => !u.members.some(connected);
  for (const level of levels) level.sort((a, b) => {
    const ia = isolated(a) ? 1 : 0;
    const ib = isolated(b) ? 1 : 0;
    if (ia !== ib) return ia - ib; // unconnected people go after the families on their row
    return byAge(a, b);
  });
  const orderIndex = new Map<string, number>();
  const setOrders = () => {
    for (const level of levels) level.forEach((u, i) => { u.order = i; orderIndex.set(u.id, i); });
  };
  setOrders();

  // An odd number of sweeps, so the last one runs top → bottom: every row ends
  // up ordered by its parents' positions with siblings oldest → youngest.
  for (let pass = 0; pass < 7; pass++) {
    const down = pass % 2 === 0;
    const range = down ? [...levels.keys()] : [...levels.keys()].reverse();
    for (const li of range) {
      const level = levels[li];
      if (!level.length) continue;
      const bary = new Map<string, number>();
      for (const u of level) {
        const neighbours = down ? unitParents(u) : unitChildren(u);
        const positions = neighbours.map((n) => orderIndex.get(n)).filter((x): x is number => x !== undefined);
        bary.set(u.id, positions.length ? positions.reduce((a, b) => a + b, 0) / positions.length : orderIndex.get(u.id) ?? 0);
      }
      level.sort((a, b) => {
        const d = (bary.get(a.id) ?? 0) - (bary.get(b.id) ?? 0);
        if (Math.abs(d) > 1e-9) return d;
        // Same parents (or same children): siblings — oldest on the left.
        return byAge(a, b);
      });
      setOrders();
    }
  }

  // 4. x positions — priority method: place children under parents, then push apart
  const unitWidth = (u: Unit) => u.members.length * NODE_W + (u.members.length - 1) * PARTNER_GAP;

  // initial packing per level
  for (const level of levels) {
    let x = 0;
    for (const u of level) {
      u.x = x + unitWidth(u) / 2;
      x += unitWidth(u) + H_GAP;
    }
  }

  const centreOf = (unitIds: string[]): number | null => {
    const xs = unitIds.map((id) => units.get(id)?.x).filter((x): x is number => x !== undefined);
    return xs.length ? xs.reduce((a, b) => a + b, 0) / xs.length : null;
  };

  const relaxLevel = (level: Unit[], target: (u: Unit) => number | null) => {
    // desired positions, then a left-to-right and right-to-left pass to keep spacing
    const desired = level.map((u) => target(u) ?? u.x);
    for (let i = 0; i < level.length; i++) {
      const want = desired[i];
      const min = i === 0 ? -Infinity : level[i - 1].x + unitWidth(level[i - 1]) / 2 + H_GAP + unitWidth(level[i]) / 2;
      level[i].x = Math.max(want, min === -Infinity ? want : min);
    }
    for (let i = level.length - 2; i >= 0; i--) {
      const max = level[i + 1].x - unitWidth(level[i + 1]) / 2 - H_GAP - unitWidth(level[i]) / 2;
      if (level[i].x > max) level[i].x = max;
    }
  };

  for (let pass = 0; pass < 4; pass++) {
    // pull children under parents (top → bottom)
    for (let li = 1; li < levels.length; li++) {
      relaxLevel(levels[li], (u) => centreOf(unitParents(u)));
    }
    // pull parents over children (bottom → top)
    for (let li = levels.length - 2; li >= 0; li--) {
      relaxLevel(levels[li], (u) => centreOf(unitChildren(u)));
    }
  }

  // 5. build node positions
  const nodes = new Map<UUID, LaidOutNode>();
  for (const u of units.values()) {
    const w = unitWidth(u);
    let x = u.x - w / 2;
    for (const m of u.members) {
      nodes.set(m, {
        id: m,
        x: x + NODE_W / 2,
        y: u.level * (NODE_H + V_GAP) + NODE_H / 2,
        level: u.level,
        unitId: u.id,
        floatSeed: hashSeed(m),
      });
      x += NODE_W + PARTNER_GAP;
    }
  }

  // 6. edges
  const edges = buildEdges(graph, nodes, relationships);

  // 7. bounds
  const bounds = boundsOfNodes(nodes);
  if (!bounds) return empty;

  return {
    nodes,
    edges,
    width: bounds.width,
    height: bounds.height,
    levels: levels.length,
    minX: bounds.minX,
    minY: bounds.minY,
  };
}

/** Bounding box of a set of node boxes (each NODE_W × NODE_H around its centre). */
export function boundsOfNodes(nodes: Map<UUID, { x: number; y: number }>): { minX: number; minY: number; width: number; height: number } | null {
  let minX = Infinity;
  let maxX = -Infinity;
  let minY = Infinity;
  let maxY = -Infinity;
  for (const n of nodes.values()) {
    minX = Math.min(minX, n.x - NODE_W / 2);
    maxX = Math.max(maxX, n.x + NODE_W / 2);
    minY = Math.min(minY, n.y - NODE_H / 2);
    maxY = Math.max(maxY, n.y + NODE_H / 2);
  }
  if (!nodes.size || !Number.isFinite(minX)) return null;
  return { minX, minY, width: maxX - minX, height: maxY - minY };
}

/**
 * Connector paths for a set of positioned people. Separate from the placement
 * so the tree can redraw the lines from wherever the circles are *right now*
 * (a grown circle pushes its neighbours a little — see treeRelax.ts) and they
 * always meet the circles.
 */
export function buildEdges(graph: FamilyGraph, nodes: Map<UUID, { x: number; y: number }>, relationships: Relationship[]): LaidOutEdge[] {
  const idSet = new Set(nodes.keys());
  const edges: LaidOutEdge[] = [];
  const seenPartner = new Set<string>();
  for (const rel of relationships) {
    if (!idSet.has(rel.fromPersonId) || !idSet.has(rel.toPersonId)) continue;
    if (rel.status === 'rejected') continue;
    const a = nodes.get(rel.fromPersonId);
    const b = nodes.get(rel.toPersonId);
    if (!a || !b) continue;
    const pending = rel.status === 'pending' || rel.status === 'disputed';
    if (rel.type === 'partner') {
      const key = partnerKey(rel.fromPersonId, rel.toPersonId);
      if (seenPartner.has(key)) continue;
      seenPartner.add(key);
      const y = (a.y + b.y) / 2 + AVATAR_DY;
      const [l, r] = a.x <= b.x ? [a, b] : [b, a];
      // Lines run to the circles' centres and pass underneath them (the edges layer sits below the
      // people), so they always reach the circle whatever size it is drawn at — grown, settled, or far out.
      const d =
        Math.abs(a.y - b.y) < 1
          ? `M ${l.x} ${y} L ${r.x} ${y}`
          : `M ${l.x} ${l.y + AVATAR_DY} C ${(l.x + r.x) / 2} ${l.y + AVATAR_DY}, ${(l.x + r.x) / 2} ${r.y + AVATAR_DY}, ${r.x} ${r.y + AVATAR_DY}`;
      edges.push({
        id: rel.id, kind: 'partner', from: rel.fromPersonId, to: rel.toPersonId, relationship: rel, d,
        mid: { x: (l.x + r.x) / 2, y }, pending, dashed: rel.current === false,
      });
    } else {
      // Parent → child: down from the parent (or the couple's midpoint), across, and down into the child.
      const parentNode = a;
      const childNode = b;
      const co = coParent(graph, nodes, rel.fromPersonId, rel.toPersonId, idSet);
      const startX = co?.x ?? parentNode.x;
      // From a couple the line grows out of the bond between them (its midpoint — even when the two sit at
      // different heights in the by-age arrangement); from a single parent, out of their circle (from its
      // centre, under the photo — so it always meets the rim whatever size the circle is drawn at).
      const startY = co ? co.y + AVATAR_DY + 6 : parentNode.y + AVATAR_DY;
      // Into the centre of the child's circle (hidden under the photo), so it reaches the circle at any size.
      const endY = childNode.y + AVATAR_DY;
      const midY = startY + (endY - startY) * 0.5;
      // A soft S-curve instead of a right-angled connector: the tree reads as grown, not wired.
      const d = `M ${startX} ${startY} C ${startX} ${midY}, ${childNode.x} ${midY}, ${childNode.x} ${endY}`;
      edges.push({
        id: rel.id, kind: 'parent', from: rel.fromPersonId, to: rel.toPersonId, relationship: rel, d,
        mid: { x: (startX + childNode.x) / 2, y: midY }, pending,
        dashed: rel.subtype !== 'biological' && rel.subtype !== 'unspecified',
      });
    }
  }
  return edges;
}

/** Midpoint between two parents of the same child (the bond between them), so sibling lines share one stem. */
function coParent(graph: FamilyGraph, nodes: Map<UUID, { x: number; y: number }>, parentId: UUID, childId: UUID, visible: Set<UUID>): { x: number; y: number } | null {
  const others = parents(graph, childId).filter((p) => p !== parentId && visible.has(p) && partners(graph, parentId).includes(p));
  if (!others.length) return null;
  const a = nodes.get(parentId);
  const b = nodes.get(others[0]);
  if (!a || !b) return null;
  return { x: (a.x + b.x) / 2, y: (a.y + b.y) / 2 };
}
